/**
 * Structured eligibility criteria — the central modelling artifact of this system.
 *
 * ClinicalTrials.gov stores eligibility as a single free-text blob. Turning that
 * blob into a list of individually-evaluable, individually-explainable criteria is
 * the hard problem the product exists to solve, and this file defines what the
 * output of that parse looks like.
 *
 * Two properties of this schema matter more than the rest:
 *
 *   1. `text` is always the verbatim source sentence. Every structured field is a
 *      lossy interpretation of it, so the original stays attached forever and is
 *      what the UI shows a clinician. The structure is for the machine; the text
 *      is for the human who has to sign their name to the decision.
 *   2. `machineCheckable` is honest. A criterion we cannot evaluate is marked as
 *      such and routed to a human, rather than being scored as satisfied because
 *      the parser found nothing to object to. Silent optimism here is the single
 *      most dangerous failure mode available to us.
 */
import { z } from 'zod'
import {
  ComparatorSchema,
  CriterionKindSchema,
  CriterionTypeSchema,
  SexSchema,
  isHumanOnlyKind,
  type CriterionKind,
} from './vocab.js'
import { CodingSchema, IsoInstantSchema, ProbabilitySchema, idSchema } from './zod-util.js'
import type { CriterionId, TrialId } from './ids.js'

/**
 * A temporal qualifier — "within 6 months", "at least 4 weeks since", "no prior".
 *
 * Temporality is broken out rather than left in the text because it is where
 * naive matchers fail most often: "prior therapy with a checkpoint inhibitor"
 * and "therapy with a checkpoint inhibitor within 28 days" select nearly
 * disjoint patient sets from the same medication list.
 */
export const TemporalWindowSchema = z.object({
  /** `WITHIN` = the event must have happened in the window; `SINCE` = the window must have elapsed since it. */
  relation: z.enum(['WITHIN', 'SINCE', 'EVER', 'NEVER', 'CURRENT']),
  /** Window length in days. Absent for `EVER` / `NEVER` / `CURRENT`. */
  days: z.number().int().positive().optional(),
  /** The verbatim phrase this was derived from, for display and for debugging the parser. */
  sourcePhrase: z.string().default(''),
})
export type TemporalWindow = z.infer<typeof TemporalWindowSchema>

/**
 * A numeric threshold, e.g. `ANC >= 1500 /mm3` or `age BETWEEN 18 AND 75 years`.
 *
 * `unit` is mandatory. Comparing a value to a threshold across different units is
 * the mg/dL-vs-mmol/L class of error, which produces a confidently wrong
 * eligibility answer rather than a crash, so the type system refuses to let a
 * threshold exist without its unit.
 */
export const ThresholdSchema = z
  .object({
    comparator: ComparatorSchema,
    value: z.number(),
    /** Upper bound; required when and only when `comparator` is `BETWEEN`. */
    valueHigh: z.number().optional(),
    unit: z.string().min(1),
  })
  .refine((t) => (t.comparator === 'BETWEEN') === (t.valueHigh !== undefined), {
    message: 'valueHigh is required for BETWEEN and forbidden otherwise',
    path: ['valueHigh'],
  })
export type Threshold = z.infer<typeof ThresholdSchema>

/**
 * One parsed eligibility criterion.
 */
export const StructuredCriterionSchema = z.object({
  id: idSchema<CriterionId>('criterion id'),
  trialId: idSchema<TrialId>('trial id'),

  /** Inclusion or exclusion. Determines the sign of the criterion's contribution. */
  type: CriterionTypeSchema,
  kind: CriterionKindSchema,

  /** The verbatim sentence from the trial's eligibility text. Never rewritten. */
  text: z.string().min(1),
  /**
   * A plain-language restatement for the patient view, at roughly a 6th-grade
   * reading level. Optional because it is generated separately and reviewed —
   * `text` is always safe to show, a paraphrase is not.
   */
  plainLanguage: z.string().optional(),

  /** Order within the trial's criteria list, so the UI can show them as authored. */
  ordinal: z.number().int().nonnegative(),

  /** Normalized concepts this criterion refers to (conditions, labs, drugs, genes). */
  codes: z.array(CodingSchema).default([]),

  /** Numeric bound, for `LAB`, `AGE`, and `PERFORMANCE_STATUS` kinds. */
  threshold: ThresholdSchema.optional(),
  /** Time qualifier, if the criterion has one. */
  temporal: TemporalWindowSchema.optional(),
  /** Sex restriction, for `SEX` criteria. */
  sex: SexSchema.optional(),

  /**
   * True when the criterion's clinical meaning is negated relative to its text —
   * "no history of seizure" parsed into a `CONDITION` criterion about seizure.
   *
   * Kept separate from `type` because the two compose and are independently
   * wrong-able: an *exclusion* criterion reading "no prior chemotherapy" is a
   * double negative, and collapsing that into a single field is how a matcher
   * ends up excluding exactly the patients it should include.
   */
  negated: z.boolean().default(false),

  /**
   * Whether an automated evaluator can reach a verdict on this criterion at all.
   * False for investigator discretion, willingness/logistics, and anything the
   * parser could not resolve into codes or a threshold.
   */
  machineCheckable: z.boolean(),

  /** Parser confidence in this structuring, in [0, 1]. Low confidence is shown in the UI. */
  parseConfidence: ProbabilitySchema,

  /**
   * How this criterion was produced. `RULE` is the deterministic parser, `LLM` is
   * model-assisted structuring, `HUMAN` is a coordinator's correction. Human
   * corrections outrank everything and are the seed corpus for parser evaluation.
   */
  provenance: z.enum(['RULE', 'LLM', 'HUMAN']),

  /**
   * Observed screen-failure rate for this criterion, once we have real outcomes.
   * A criterion that fails 40% of the patients who reach a screening visit is
   * worth checking first, and worth weighting heavily in the reranker.
   */
  observedFailureRate: ProbabilitySchema.optional(),

  parsedAt: IsoInstantSchema,
})
export type StructuredCriterion = z.infer<typeof StructuredCriterionSchema>

/**
 * The full parse of one trial's eligibility text, including what the parser could
 * not handle.
 *
 * `unparsedSegments` is a first-class field, not an error channel. It is the
 * honest measure of coverage, it drives the "N criteria need human review" badge,
 * and it is the backlog for improving the parser.
 */
export const CriteriaParseSchema = z.object({
  trialId: idSchema<TrialId>('trial id'),
  criteria: z.array(StructuredCriterionSchema),
  /** Source text segments the parser declined to structure, with why. */
  unparsedSegments: z
    .array(z.object({ text: z.string(), reason: z.string() }))
    .default([]),
  /** Fraction of source sentences that produced at least one structured criterion. */
  coverage: ProbabilitySchema,
  parserVersion: z.string().min(1),
  parsedAt: IsoInstantSchema,
})
export type CriteriaParse = z.infer<typeof CriteriaParseSchema>

// ---------------------------------------------------------------------------
// Derived helpers
// ---------------------------------------------------------------------------

/**
 * Criteria that must hold or the patient is out, regardless of how well
 * everything else fits.
 *
 * Only structural, high-confidence, machine-checkable criteria qualify. An
 * exclusion the parser was unsure about does not get to veto a candidate — it
 * gets flagged for a human. This is the same recall-first asymmetry that governs
 * the Stage 0 filters: an unrecoverable false exclusion is worse than a reviewed
 * false inclusion.
 */
export function isBlockingCriterion(criterion: StructuredCriterion): boolean {
  if (!criterion.machineCheckable) return false
  if (criterion.parseConfidence < 0.8) return false
  if (isHumanOnlyKind(criterion.kind)) return false
  const structural: readonly CriterionKind[] = ['AGE', 'SEX']
  return structural.includes(criterion.kind)
}

/** Criteria a human has to resolve, in the order a coordinator should work them. */
export function humanReviewQueue(criteria: readonly StructuredCriterion[]): StructuredCriterion[] {
  return criteria
    .filter((c) => !c.machineCheckable || c.parseConfidence < 0.6)
    .sort((a, b) => {
      // Exclusions first (they end the review fastest), then by parser doubt.
      if (a.type !== b.type) return a.type === 'EXCLUSION' ? -1 : 1
      return a.parseConfidence - b.parseConfidence
    })
}

/**
 * A rough "how hard is this trial to enroll" score in [0, 1], from the shape of
 * its criteria alone. Used to sort the sponsor's feasibility view and as a
 * reranker feature: a trial with 40 criteria and 12 lab thresholds is a different
 * proposition from one with 6 criteria.
 */
export function criteriaBurden(criteria: readonly StructuredCriterion[]): number {
  if (criteria.length === 0) return 0
  const count = criteria.length
  const thresholds = criteria.filter((c) => c.threshold !== undefined).length
  const exclusions = criteria.filter((c) => c.type === 'EXCLUSION').length
  const unresolvable = criteria.filter((c) => !c.machineCheckable).length
  // Saturating combination — 30+ criteria is already "very restrictive", and the
  // difference between 30 and 60 does not matter to a coordinator's decision.
  const raw = count / 30 + thresholds / 12 + exclusions / 20 + unresolvable / 10
  return Math.min(1, raw / 4)
}
